/**
 * Origin parsing and comparison, kept pure and free of `import.meta.env` so the
 * tests in origin.test.ts can exercise it without the app bootstrap.
 *
 * The consumer that matters is the bearer-token routing in src/api/api.ts, which
 * decides between two resources' tokens by asking whether a request URL belongs to
 * the 125 platform origin. Both failure directions are silent and bad — the platform
 * token leaking onto every host, or the 125 section never being authorised — so the
 * comparison lives somewhere it can be asserted rather than inferred.
 */

/** The origin of `base`, or null when it is absent or not a parseable absolute URL. */
export const parseOrigin = (base: string | undefined | null): string | null => {
	if (!base) {
		return null;
	}

	try {
		return new URL(base).origin;
	} catch {
		return null;
	}
};

/**
 * True when `url` belongs to `origin`.
 *
 * Fails closed on every uncertainty: a null origin (nothing configured), a url that
 * will not parse, or a relative url with no base to resolve it against. A caller
 * that cannot tell must not be told yes, because yes is what hands over a token.
 */
export const isSameOrigin = (
	url: string,
	origin: string | null,
	fallbackBase?: string,
): boolean => {
	if (!origin) {
		return false;
	}

	try {
		return new URL(url, fallbackBase).origin === origin;
	} catch {
		return false;
	}
};
